import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { getDealDetail } from "@/lib/deals.functions";
import {
  listDealAgentDartFlags,
  setDealAgentDartEnabled,
  listDealGoals,
  listDealSendModes,
  setDealSendMode,
  listAgentActions,
} from "@/lib/agent-actions.functions";
import { getMailboxReadiness } from "@/lib/mailbox-readiness.functions";
import { getAgentDartStatus } from "@/lib/agent-status.functions";
import { getMyAdminStatus } from "@/lib/billing.functions";
import { runAgentTickForDealAdmin } from "@/lib/agent-tick.functions";
import { AgentStatusStrip } from "@/components/agent/agent-status-strip";
import { AgentActivityFeed } from "@/components/agent/agent-activity-feed";
import { AgentUpcomingMeetings } from "@/components/agent/agent-upcoming-meetings";
import { Link } from "@tanstack/react-router";
import { DealPageHeader } from "@/components/deal-page-header";
import { DealNav } from "@/components/deal-nav";
import { DealGoalDialog } from "@/components/dashboard/deal-card";
import { PlanDialog } from "@/components/agent/plan-dialog";


import {
  OutreachLimitsRow,
  DailyOutreachApproval,
} from "@/components/agent/daily-outreach-card";
import { LinkedInQueueCard } from "@/components/agent/linkedin-queue-list";
import { WarmPathsCard } from "@/components/agent/warm-paths-card";
import { CallOutcomeCard } from "@/components/agent/call-outcome-card";
import { listLinkedInQueue } from "@/lib/linkedin-queue.functions";
import { AgentDartSetupPanel } from "@/components/agent/setup-checklist";
import { getAgentDartSetup } from "@/lib/agentdart-setup.functions";
import { SETUP_PANEL_ANCHOR, requestSetupPanel } from "@/lib/agentdart-setup";
import { getAgentDartStats } from "@/lib/agent-stats.functions";
import { getDailyOutreach } from "@/lib/scheduled-outreach.functions";
import {
  AgentDartTabs,
  AgentDartSection,
  isAgentDartTabKey,
  type AgentDartTabKey,
} from "@/components/agent/agentdart-sections";
import {
  AgentGoalCard,
  ReplaceGoalWithPlanDialog,
} from "@/components/agent/agent-goal-card";
import {
  getRecommendedPlan,
  applyRecommendedPlan,
  recomputeRecommendedPlan,
} from "@/lib/agentdart-plan.functions";
import { FULL_RAISE_PLAN_VERSION } from "@/lib/agentdart-plan-version";
import { AgentStatsCard } from "@/components/agent/agent-stats-card";
import {
  DealSecondSigner,
  type SecondSigner,
} from "@/components/deal-second-signer";

export const Route = createFileRoute("/_authed/deals/$dealId_/agentdart")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { section?: AgentDartTabKey } =>
    isAgentDartTabKey(search["section"]) ? { section: search["section"] } : {},
  head: () => ({
    meta: [
      { title: "AgentDart: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DealAgentDartPage,
});

function DealAgentDartPage() {
  const { dealId } = Route.useParams();
  const { section } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { user } = useAuth();
  const qc = useQueryClient();

  const fetchDetail = useServerFn(getDealDetail);
  const detail = useQuery({
    queryKey: ["deal-detail", dealId],
    queryFn: () => fetchDetail({ data: { dealId } }),
  });
  const isDemo =
    (detail.data?.deal as { is_demo?: boolean } | undefined)?.is_demo === true;

  // Same team-batched queries and keys as the Overview tab, so invalidations
  // flow both ways.
  const fetchDealAgentDartFlags = useServerFn(listDealAgentDartFlags);
  const dealAgentDartFlags = useQuery({
    queryKey: ["agentdart-deal-flags", user?.id],
    queryFn: () => fetchDealAgentDartFlags({}),
    enabled: !!user,
  });
  const agentDartEnabled = React.useMemo(
    () =>
      (dealAgentDartFlags.data?.deals ?? []).find((d) => d.id === dealId)
        ?.enabled ?? false,
    [dealAgentDartFlags.data, dealId],
  );

  const fetchDealGoals = useServerFn(listDealGoals);
  const dealGoals = useQuery({
    queryKey: ["deal-goals", user?.id],
    queryFn: () => fetchDealGoals({}),
    enabled: !!user,
  });
  const goal =
    (dealGoals.data?.goals ?? []).find((g) => g.deal_id === dealId) ?? null;

  const fetchSendModes = useServerFn(listDealSendModes);
  const sendModes = useQuery({
    queryKey: ["deal-send-modes", user?.id],
    queryFn: () => fetchSendModes({}),
    enabled: !!user,
  });
  const sendMode = ((sendModes.data?.deals ?? []).find((d) => d.id === dealId)?.mode ??
    "drafts") as "drafts" | "direct";

  const fetchReadiness = useServerFn(getMailboxReadiness);
  const readiness = useQuery({
    queryKey: ["mailbox-readiness", user?.id],
    queryFn: () => fetchReadiness({}),
    enabled: !!user,
  });
  const mailboxConnected = readiness.data?.ready === true;
  const gmailNeedsReconnect = readiness.data?.needs_reconnect === true;

  const updateSendMode = useServerFn(setDealSendMode);
  const handleSendModeChange = async (mode: "drafts" | "direct") => {
    const key = ["deal-send-modes", user?.id];
    const prev = qc.getQueryData(key);
    qc.setQueryData(key, (old: { deals: { id: string; mode: string }[] } | undefined) =>
      old
        ? { deals: old.deals.map((d) => (d.id === dealId ? { ...d, mode } : d)) }
        : old,
    );
    try {
      await updateSendMode({ data: { dealId, mode } });
      void qc.invalidateQueries({ queryKey: ["agent-actions"] });
    } catch (e) {
      qc.setQueryData(key, prev);
      toast.error(e instanceof Error ? e.message : "Failed to update this setting");
    }
  };

  const fetchStatus = useServerFn(getAgentDartStatus);
  const statusQuery = useQuery({
    queryKey: ["agentdart-status", dealId],
    queryFn: () => fetchStatus({ data: { dealId } }),
    enabled: !!user && !isDemo,
    staleTime: 30_000,
  });

  const fetchAdmin = useServerFn(getMyAdminStatus);
  const adminQuery = useQuery({
    queryKey: ["my-admin", user?.id],
    queryFn: () => fetchAdmin(),
    enabled: !!user,
    staleTime: 5 * 60_000,
  });

  const fetchActionCount = useServerFn(listAgentActions);
  const actionsQuery = useQuery({
    queryKey: ["agent-actions", dealId],
    queryFn: () => fetchActionCount({ data: { dealId } }),
    enabled: !!user && !isDemo,
    staleTime: 15_000,
  });
  const pendingCount = actionsQuery.data?.pending?.length ?? 0;

  const fetchLinkedIn = useServerFn(listLinkedInQueue);
  const linkedInQuery = useQuery({
    queryKey: ["linkedin-queue", dealId],
    queryFn: () => fetchLinkedIn({ data: { dealId } }),
    enabled: !!user && !isDemo,
    staleTime: 15_000,
  });
  const linkedInCount = linkedInQuery.data?.rows?.length ?? 0;

  const runTick = useServerFn(runAgentTickForDealAdmin);
  const [checking, setChecking] = React.useState(false);
  const handleRunCheck = async () => {
    setChecking(true);
    try {
      const res = await runTick({ data: { dealId } });
      toast.success(
        res.skipped
          ? `No check ran: ${res.skipped}`
          : `Check finished. ${res.inserted} queued.`,
      );
      void qc.invalidateQueries({ queryKey: ["agentdart-status", dealId] });
      void qc.invalidateQueries({ queryKey: ["agent-events", dealId] });
      void qc.invalidateQueries({ queryKey: ["agent-actions"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "The check could not run");
    } finally {
      setChecking(false);
    }
  };

  const fetchSetup = useServerFn(getAgentDartSetup);
  const setupQuery = useQuery({
    queryKey: ["agentdart-setup", dealId, user?.id],
    queryFn: () => fetchSetup({ data: { dealId } }),
    enabled: !!user && !isDemo,
    staleTime: 15_000,
  });
  const setupComplete =
    !!setupQuery.data && setupQuery.data.done === setupQuery.data.total;

  const fetchStats = useServerFn(getAgentDartStats);
  const statsQuery = useQuery({
    queryKey: ["agentdart-stats", dealId],
    queryFn: () => fetchStats({ data: { dealId } }),
    enabled: !!user && !isDemo,
    staleTime: 30_000,
  });
  const stats = statsQuery.data ?? null;

  const fetchDailyOutreach = useServerFn(getDailyOutreach);
  const dailyOutreach = useQuery({
    queryKey: ["daily-outreach", dealId],
    queryFn: () => fetchDailyOutreach({ data: { dealId } }),
    enabled: !!user && !isDemo,
    staleTime: 15_000,
  });

  const [setupOpen, setSetupOpen] = React.useState(false);
  const setupCollapsed = setupComplete && !setupOpen;
  const limitsSummary = dailyOutreach.data
    ? `Setup complete. Limits: ${dailyOutreach.data.cap} emails a day at ${dailyOutreach.data.time}, ${dailyOutreach.data.linkedin_cap} LinkedIn, ${dailyOutreach.data.warm_intro_weekly_cap} intro requests a week (after a call), ${sendMode === "direct" ? "sends directly" : "drafts only"}`
    : "Setup complete.";

  // Default tab: whatever needs attention first. Setup while it is
  // unfinished, otherwise the queue of things waiting on the founder.
  const defaultTab: AgentDartTabKey =
    setupQuery.data && setupQuery.data.done < setupQuery.data.total
      ? "setup"
      : "needs";
  const activeTab: AgentDartTabKey = section ?? defaultTab;
  const selectTab = (key: AgentDartTabKey) => {
    void navigate({ search: { section: key }, params: { dealId } });
  };

  const scrollToSetup = () => {
    setSetupOpen(true);
    selectTab("setup");
    requestAnimationFrame(() => {
      document
        .getElementById(SETUP_PANEL_ANCHOR)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const [goalDialogOpen, setGoalDialogOpen] = React.useState(false);
  const [goalDialogPicker, setGoalDialogPicker] = React.useState(false);
  const [planDialogOpen, setPlanDialogOpen] = React.useState(false);
  const isFullRaiseGoal = stats?.goal.goal_type === "full_raise";
  /** Adjust opens the plan dialog for a whole raise goal, the goal form otherwise. */
  const openAdjust = () => {
    if (isFullRaiseGoal) {
      setPlanDialogOpen(true);
      return;
    }
    setGoalDialogPicker(false);
    setGoalDialogOpen(true);
  };


  // Only fetched when the deal has no goal, which is when the card offers
  // the recommended plan.
  const [replacePlanOpen, setReplacePlanOpen] = React.useState(false);
  const fetchPlan = useServerFn(getRecommendedPlan);
  const planQuery = useQuery({
    queryKey: ["agentdart-recommended-plan", dealId],
    queryFn: () => fetchPlan({ data: { dealId } }),
    enabled:
      !!user &&
      !isDemo &&
      ((!!statsQuery.data && !statsQuery.data.goal.label) || replacePlanOpen),
    staleTime: 60_000,
  });
  const applyPlanFn = useServerFn(applyRecommendedPlan);
  const [applyingPlan, setApplyingPlan] = React.useState(false);
  const usePlan = async () => {
    setApplyingPlan(true);
    try {
      const res = await applyPlanFn({ data: { dealId } });
      toast.success(res.rationale[0] ?? "Plan set.");
      void qc.invalidateQueries({ queryKey: ["agentdart-stats", dealId] });
      void qc.invalidateQueries({ queryKey: ["deal-goals"] });
      void qc.invalidateQueries({ queryKey: ["daily-outreach", dealId] });
      setReplacePlanOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not set the plan");
    } finally {
      setApplyingPlan(false);
    }
  };

  const recomputePlanFn = useServerFn(recomputeRecommendedPlan);
  const [recomputingPlan, setRecomputingPlan] = React.useState(false);
  const recomputePlan = async () => {
    setRecomputingPlan(true);
    try {
      await recomputePlanFn({ data: { dealId } });
      toast.success("Plan recomputed.");
      void qc.invalidateQueries({ queryKey: ["agentdart-stats", dealId] });
      void qc.invalidateQueries({ queryKey: ["deal-goals"] });
      void qc.invalidateQueries({ queryKey: ["daily-outreach", dealId] });
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Could not recompute the plan",
      );
    } finally {
      setRecomputingPlan(false);
    }
  };

  const setDealAgentDart = useServerFn(setDealAgentDartEnabled);
  const handleAgentDartToggle = async (id: string, enabled: boolean) => {
    const flagsKey = ["agentdart-deal-flags", user?.id];
    const prev = qc.getQueryData(flagsKey);
    qc.setQueryData(
      flagsKey,
      (old: { deals: { id: string; name: string; enabled: boolean }[] } | undefined) =>
        old
          ? { deals: old.deals.map((d) => (d.id === id ? { ...d, enabled } : d)) }
          : old,
    );
    try {
      await setDealAgentDart({ data: { dealId: id, enabled } });
      if (enabled) requestSetupPanel(id);
      void qc.invalidateQueries({ queryKey: ["agentdart-setup", id] });
      toast.success(
        enabled
          ? "AgentDart is watching this deal. You will get a short daily email when it has follow-ups ready."
          : "AgentDart is off for this deal",
      );
      void qc.invalidateQueries({ queryKey: ["agent-actions"] });
      void qc.invalidateQueries({ queryKey: ["agent-actions-count"] });
    } catch (e) {
      qc.setQueryData(flagsKey, prev);
      toast.error(e instanceof Error ? e.message : "Failed to update AgentDart");
    }
  };

  const nothingWaiting =
    pendingCount === 0 &&
    linkedInCount === 0 &&
    (stats?.outreach.approved_waiting ?? 0) === 0 &&
    (stats?.outreach.eligible ?? 0) === 0;


  return (
    <main className="mx-auto max-w-[640px] px-4 pb-16 pt-6 lg:max-w-[1320px]">
      <div className="space-y-6">
        <DealPageHeader dealId={dealId} />
        <DealNav dealId={dealId} active="agentdart" isDemo={isDemo} />
        {!isDemo && (
          <AgentDartTabs
            active={activeTab}
            onSelect={selectTab}
            counts={{ needs: pendingCount + linkedInCount }}
          />
        )}
        {!isDemo && (
          <AgentStatusStrip
            scope="deal"
            status={statusQuery.data ?? null}
            loading={statusQuery.isLoading}
            goalLabel={goal?.focus ?? null}
            progressLabel={goal?.progress_label ?? null}
            sendMode={sendMode}
            mailboxLabel={
              readiness.data?.account_email
                ? `${readiness.data.provider === "gmail" ? "Gmail" : "Outlook"}, ${readiness.data.account_email}`
                : "No mailbox connected"
            }
            onPauseToggle={() => void handleAgentDartToggle(dealId, !agentDartEnabled)}
            onAdjustGoal={openAdjust}
            onRunCheck={adminQuery.data?.isAdmin ? () => void handleRunCheck() : undefined}
            runningCheck={checking}
            setupDone={setupQuery.data?.done ?? null}
            setupTotal={setupQuery.data?.total ?? null}
            onSetupClick={scrollToSetup}
          />
        )}

        {!isDemo && activeTab === "setup" && (
          <AgentDartSection id="agentdart-setup" title="Setup and limits">
            {setupCollapsed ? (
              <div
                id={SETUP_PANEL_ANCHOR}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brand-line bg-brand-paper px-4 py-2"
              >
                <p className="text-[14px] text-brand-muted">{limitsSummary}</p>
                <button
                  type="button"
                  onClick={() => setSetupOpen(true)}
                  className="text-[13px] text-brand-ink underline underline-offset-4"
                >
                  Show
                </button>
              </div>
            ) : (
              <>
                <AgentDartSetupPanel
                  dealId={dealId}
                  agentDartEnabled={agentDartEnabled}
                  sendMode={sendMode}
                  mailboxConnected={mailboxConnected}
                  gmailNeedsReconnect={gmailNeedsReconnect}
                  onToggleAgentDart={(enabled) =>
                    void handleAgentDartToggle(dealId, enabled)
                  }
                  onSendModeChange={handleSendModeChange}
                />
                {agentDartEnabled ? null : (
                  <section className="space-y-2 rounded-lg border border-brand-line bg-brand-paper px-4 py-3">
                    <p className="text-[14px] text-brand-muted">
                      AgentDart is off for this deal. Turn it on for daily
                      follow-up drafts and a goal you can track.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        void handleAgentDartToggle(dealId, true);
                        setGoalDialogOpen(true);
                      }}
                      className="rounded-md border border-brand-accent px-2 py-1 text-[13px] font-medium text-brand-accent hover:bg-brand-accent/10"
                    >
                      Activate AgentDart
                    </button>
                  </section>
                )}
                <DealSecondSigner
                  dealId={dealId}
                  value={
                    (
                      detail.data?.deal as
                        | { second_signer?: SecondSigner | null }
                        | undefined
                    )?.second_signer ?? null
                  }
                />
                {agentDartEnabled ? <OutreachLimitsRow dealId={dealId} /> : null}
                {setupComplete ? (
                  <button
                    type="button"
                    onClick={() => setSetupOpen(false)}
                    className="text-[13px] text-brand-ink underline underline-offset-4"
                  >
                    Hide
                  </button>
                ) : null}
              </>
            )}
          </AgentDartSection>
        )}

        {!isDemo && activeTab === "goal" && (
          <AgentDartSection id="agentdart-goal" title="Goal">
            <AgentGoalCard
              goal={stats?.goal ?? null}
              loading={statsQuery.isPending}
              isError={statsQuery.isError}
              onRetry={() => void statsQuery.refetch()}
              onAdjust={openAdjust}

              onReplace={() => {
                setGoalDialogPicker(true);
                setGoalDialogOpen(true);
              }}
              limits={
                dailyOutreach.data
                  ? {
                      cap: dailyOutreach.data.cap,
                      effective_cap: dailyOutreach.data.effective_cap,
                      time: dailyOutreach.data.time,
                      linkedin_cap: dailyOutreach.data.linkedin_cap,
                      warm_intro_weekly_cap:
                        dailyOutreach.data.warm_intro_weekly_cap,
                    }
                  : null
              }
              plan={planQuery.data ?? null}
              planLoading={planQuery.isPending}
              planError={planQuery.isError}
              planApplying={applyingPlan}
              onUsePlan={() => void usePlan()}
              onUseRecommendedPlan={
                stats?.goal.label && stats.goal.goal_type !== "full_raise"
                  ? () => setReplacePlanOpen(true)
                  : undefined
              }
              onRecomputePlan={() => void recomputePlan()}
              recomputing={recomputingPlan}
              planVersionCurrent={FULL_RAISE_PLAN_VERSION}
            />
            <ReplaceGoalWithPlanDialog
              open={replacePlanOpen}
              onOpenChange={setReplacePlanOpen}
              goalLabel={stats?.goal.label ?? "your goal"}
              plan={planQuery.data ?? null}
              loading={planQuery.isPending}
              isError={planQuery.isError}
              applying={applyingPlan}
              onConfirm={() => void usePlan()}
            />
          </AgentDartSection>
        )}

        {!isDemo && activeTab === "stats" && (
          <AgentDartSection id="agentdart-stats" title="Stats">
            <AgentStatsCard
              stats={stats}
              loading={statsQuery.isPending}
              isError={statsQuery.isError}
              onRetry={() => void statsQuery.refetch()}
            />
            {agentDartEnabled ? (
              <WarmPathsCard dealId={dealId} collapsible />
            ) : null}
          </AgentDartSection>
        )}

        {!isDemo && activeTab === "needs" && (
          <AgentDartSection id="agentdart-needs-you" title="Needs you">
            {nothingWaiting ? (
              <p className="text-[14px] text-brand-muted">Nothing waiting on you.</p>
            ) : null}
            {pendingCount > 0 ? (
              <p className="text-[14px] text-brand-muted">
                {`${pendingCount} ${pendingCount === 1 ? "follow-up is" : "follow-ups are"} ready for your review.`}{" "}
                <Link
                  to="/agent"
                  search={{ dealId }}
                  className="text-brand-ink underline underline-offset-4"
                >
                  Open the follow-up queue
                </Link>
              </p>
            ) : null}
            {agentDartEnabled ? <DailyOutreachApproval dealId={dealId} /> : null}
            {agentDartEnabled ? <LinkedInQueueCard dealId={dealId} /> : null}
            <CallOutcomeCard dealId={dealId} />
            <AgentUpcomingMeetings dealId={dealId} collapsible />
          </AgentDartSection>
        )}

        {!isDemo && activeTab === "activity" && (
          <AgentDartSection id="agentdart-activity">
            <AgentActivityFeed dealId={dealId} raiseDay={stats?.raise_day ?? null} />
          </AgentDartSection>
        )}
        {!isDemo && (
          <PlanDialog
            dealId={dealId}
            goal={goal}
            open={planDialogOpen}
            onOpenChange={setPlanDialogOpen}
            sentCount={stats?.goal.phases?.launch.sent ?? 0}
            sendMode={sendMode}
            mailboxConnected={mailboxConnected}
            gmailNeedsReconnect={gmailNeedsReconnect}
            onSendModeChange={handleSendModeChange}
          />
        )}
        {!isDemo && (
          <DealGoalDialog
            dealId={dealId}
            dealName={(detail.data?.deal as { name?: string } | undefined)?.name ?? ""}
            goal={goal}
            open={goalDialogOpen}
            onOpenChange={setGoalDialogOpen}
            startInPicker={goalDialogPicker}
            sendMode={sendMode}
            mailboxConnected={mailboxConnected}
            gmailNeedsReconnect={gmailNeedsReconnect}
            onSendModeChange={handleSendModeChange}
          />
        )}

      </div>
    </main>
  );
}
